import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useImportJobTickets, fetchAllImportJobTickets } from "@/hooks/use-itsm-import";
import type { TicketResultStatus } from "@/lib/types/itsm-import";

interface ImportTicketsListProps {
  tenantId: string;
  jobId: string;
  failedCount: number;
  skippedCount: number;
  succeededCount: number;
  isJobActive?: boolean;
}

const STATUS_BADGE: Record<TicketResultStatus, { label: string; variant: "default" | "secondary" | "destructive"; className?: string }> = {
  succeeded: { label: "Succeeded", variant: "default", className: "bg-green-400 hover:bg-green-400" },
  failed: { label: "Failed", variant: "destructive" },
  skipped: { label: "Skipped", variant: "secondary" },
};

export function ImportTicketsList({
  tenantId,
  jobId,
  failedCount,
  skippedCount,
  succeededCount,
  isJobActive = false,
}: ImportTicketsListProps) {
  const [statusFilter, setStatusFilter] = useState<TicketResultStatus | "all">(
    failedCount > 0 ? "failed" : "all",
  );
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);

  const queryStatus = statusFilter === "all" ? undefined : statusFilter;
  const { data, isLoading } = useImportJobTickets(tenantId, jobId, queryStatus, page, 50, isJobActive);

  const totalPages = data ? Math.ceil(data.total / data.page_size) : 0;

  const handleTabChange = (value: string) => {
    setStatusFilter(value as TicketResultStatus | "all");
    setPage(1);
  };

  const handleDownloadCsv = async () => {
    setDownloading(true);
    try {
      const tickets = await fetchAllImportJobTickets(tenantId, jobId, queryStatus);

      const headers = ["provider_ticket_id", "harmony_ticket_id", "status", "skip_reason", "error", "created_at"];
      const rows = tickets.map((t) => [
        t.provider_ticket_id,
        t.harmony_ticket_id ?? "",
        t.status,
        t.skip_reason ?? "",
        (t.error ?? "").replace(/"/g, '""'),
        t.created_at,
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import-${jobId}-tickets${queryStatus ? `-${queryStatus}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download tickets");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Imported Tickets</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void handleDownloadCsv(); }}
            disabled={downloading || !data?.total}
          >
            {downloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={statusFilter} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="all">
              All
            </TabsTrigger>
            <TabsTrigger value="succeeded">
              Succeeded
              {succeededCount > 0 && (
                <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs">{succeededCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="failed">
              Failed
              {failedCount > 0 && (
                <span className="ml-1.5 rounded-full bg-destructive/20 px-1.5 text-xs text-destructive">{failedCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="skipped">
              Skipped
              {skippedCount > 0 && (
                <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs">{skippedCount}</span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !data?.tickets.length ? (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
            No tickets found.
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Provider ID</TableHead>
                    <TableHead className="w-[140px]">Harmony ID</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.tickets.map((ticket) => (
                    <TableRow key={`${ticket.provider_ticket_id}-${ticket.status}`}>
                      <TableCell className="font-mono text-xs">
                        {ticket.provider_ticket_id}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {ticket.harmony_ticket_id ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE[ticket.status].variant} className={STATUS_BADGE[ticket.status].className}>
                          {STATUS_BADGE[ticket.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs whitespace-pre-wrap break-all">
                        {ticket.status === "failed" && (
                          <span className="text-destructive">{ticket.error}</span>
                        )}
                        {ticket.status === "skipped" && (
                          <span className="text-muted-foreground">{ticket.skip_reason}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Page {data.page} of {totalPages} ({data.total} tickets)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => { setPage((p) => p - 1); }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => { setPage((p) => p + 1); }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
